import { RequestLocationProvider } from "../../../../components/requests/RequestLocationContext";
import { requestPayload, requestDestinationMessage } from "../../../../components/requests/requestLocationUtils";
import React from "react";
import HomeDisplayEmployee from "../employee/home/HomeDisplayEmployee";
import AvailabilityDisplayEmployee from "../employee/availability/AvailabilityDisplayEmployee";
import TimeOffDisplayEmployee from "../employee/availability/TimeOffDisplayEmployee";
import ShiftSwapDisplayEmployee from "../employee/shiftswaps/ShiftSwapDisplayEmployee";
import TasksDisplayEmployee from "../employee/tasks/TasksDisplayEmployee";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import BackHandOutlinedIcon from "@mui/icons-material/BackHandOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { Modal, Box } from "@mui/material";
import {
  API_get_weather,
  API_get_list_time_off_requests_for_employee,
  API_get_schedule,
  API_get_shift_pool_for_employee,
  API_drop_shift,
  API_rescind_drop_shift,
  API_get_manager_shift_pickup_request_responses,
  API_get_employee_shift_pickup_request_responses,
  API_list_availability_for_employee,
  API_list_preferred_availability_for_employee,
  API_pickup_shift,
  API_rescind_pickup_shift,
  API_hide_shift_change_request,
  API_create_availability_request,
  API_rescind_availability_request,
  API_request_time_off,
  API_rescind_time_off_request,
  API_hide_time_off_request,
  API_delete_time_off_request,
  API_drawer_notifications,
  API_hide_drawer_notification,
  API_hide_all_drawer_notifications,
  API_get_notification_settings,
  API_update_notification_settings,
  API_get_roles,
  API_get_plan_tasks_for_user_and_date,
  API_save_plan_task_assignment,
  API_get_blackout_dates
} from "../../../../constants";
import { getRightworkAPI } from "../utilities/requests";
import { postRightworkAPI } from "../utilities/requests";
import { formatDate } from "../utilities/helpers";
import NotificationsDrawer from "../NotificationsDrawer";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import LoadSpinner from "../../../../utilities/LoadSpinner";
import TopBarDisplayTitle from "../../home/top_bar_components/TopBarDisplayTitle";
import { getMaxDatePlusOne } from "../utilities/helpers";

export default function RequestsEmployeeDisplay(props) {
  const [storeId, setStoreId] = React.useState(props.homeStore);
  // Tasks are a store feature (daily_plan_enabled), like on mobile: no tab,
  // no My Day link and no Tasks view where the store has it off.
  const tasksEnabled = !!props.dailyPlanEnabled;

  React.useEffect(() => {
    setStoreId(props.homeStore)
  }, [props.homeStore])

  const [data, setData] = React.useState([]);

  const [display, setDisplay] = React.useState("My Day");

  // Switching to a store with tasks off while the Tasks view is open would
  // leave the employee on a blank pane, since the tab is gone too.
  React.useEffect(() => {
    if (!tasksEnabled && display === "Tasks") setDisplay("My Day");
  }, [tasksEnabled, display]);

  const [notificationsOpen, setNotificationsOpen] = React.useState(false);

  const storeTimezone = React.useMemo(() => {
    return props.storeNames?.find((store) => store.id === props.homeStore)?.timezone || null;
  }, [props.storeNames, props.homeStore]);

  function handleNotificationClick() {
    setNotificationsOpen((prevState) => !prevState);
  }

  const notificationsRef = React.useRef(null);

  React.useEffect(() => {
    const closeNotificationsOnOutsideClick = (event) => {
      if (
        notificationsOpen &&
        notificationsRef.current &&
        !notificationsRef.current.contains(event.target)
      ) {
        setNotificationsOpen(false);
      }
    };

    document.addEventListener("click", closeNotificationsOnOutsideClick);

    return () => {
      document.removeEventListener("click", closeNotificationsOnOutsideClick);
    };
  }, [notificationsOpen]);


  // ----------- POST APIs ----------- //
  const drop_shift = async (shift_drop_id, shift_pickup_id, drop_note) => {
    // Prepare data to send
    const requestData = {
      store_id: storeId,
      shift_drop_id: shift_drop_id,
      ...(shift_pickup_id && { shift_pickup_id: shift_pickup_id }),
      drop_note: drop_note,
    };

    try {
      toast.loading("Submitting Drop Request...", { autoClose: false });

      const res = await postRightworkAPI(API_drop_shift, requestData);

      if (res && res.status === 200) {
        // Handle success
        toast.dismiss();
        toast.success("Drop Request Submitted!", { autoClose: 3000 });
        fetch_data(
          {
            shiftPool: API_get_shift_pool_for_employee,
            shiftPickupResponses:
              API_get_employee_shift_pickup_request_responses,
            schedule: API_get_schedule,
          },
          api_params
        );
      }
    } catch (error) {
      // Handle network error
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }
      console.error("Error:", error); // Handle error as needed
    }
  };

  const rescind_drop_shift = async (shift_drop_id) => {
    // Prepare data to send
    const requestData = {
      store_id: storeId,
      shift_drop_id: shift_drop_id,
    };

    try {
      toast.loading("Cancelling Drop Request...", { autoClose: false });

      const res = await postRightworkAPI(API_rescind_drop_shift, requestData);

      if (res && res.status === 200) {
        // Handle success
        toast.dismiss();
        toast.success("Drop Request Cancelled!", { autoClose: 3000 });
        fetch_data(
          {
            shiftPool: API_get_shift_pool_for_employee,
            shiftPickupResponses:
              API_get_employee_shift_pickup_request_responses,
            schedule: API_get_schedule,
          },
          api_params
        );
      }
    } catch (error) {
      // Handle network error
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }
      console.error("Error:", error); // Handle error as needed
    }
  };

  const trade_shift = async (shift_drop_id, shift_pickup_id) => {
    // Prepare data to send
    const requestData = {
      store_id: storeId,
      shift_drop_id: shift_drop_id,
      ...(shift_pickup_id && { shift_pickup_id: shift_pickup_id }),
    };

    try {
      toast.loading("Submitting Trade Request...", { autoClose: false });

      const res = await postRightworkAPI(API_drop_shift, requestData);

      if (res && res.status === 200) {
        // Handle success
        toast.dismiss();
        toast.success("Trade Request Submitted!", { autoClose: 3000 });
        fetch_data(
          {
            shiftPool: API_get_shift_pool_for_employee,
            shiftPickupResponses:
              API_get_employee_shift_pickup_request_responses,
            schedule: API_get_schedule,
          },
          api_params
        );
      }
    } catch (error) {
      // Handle network error
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }
      console.error("Error:", error); // Handle error as needed
    }
  };

  const rescind_trade_shift = async (shift_drop_id) => {
    // Prepare data to send
    const requestData = {
      store_id: storeId,
      shift_drop_id: shift_drop_id,
    };

    try {
      toast.loading("Cancelling Trade Request...", { autoClose: false });

      const res = await postRightworkAPI(API_rescind_drop_shift, requestData);

      if (res && res.status === 200) {
        // Handle success
        toast.dismiss();
        toast.success("Trade Request Cancelled!", { autoClose: 3000 });
        fetch_data(
          {
            shiftPool: API_get_shift_pool_for_employee,
            shiftPickupResponses:
              API_get_employee_shift_pickup_request_responses,
            schedule: API_get_schedule,
          },
          api_params
        );
      }
    } catch (error) {
      // Handle network error
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }
      console.error("Error:", error); // Handle error as needed
    }
  };

  const respond_to_trade_request = async (
    shift_drop_id,
    shift_change_request_uuid,
    shift_change_request_response
  ) => {
    // Prepare data to send
    const requestData = {
      store_id: storeId,
      shift_drop_id: shift_drop_id,
      shift_change_request_uuid: shift_change_request_uuid,
      shift_change_request_response: shift_change_request_response,
    };

    try {
      toast.loading("Submitting Shift Trade Request...", { autoClose: false });

      const res = await postRightworkAPI(API_pickup_shift, requestData);

      if (res && res.status === 200) {
        // Handle success
        toast.dismiss();
        toast.success("Shift Trade Request Submitted!", { autoClose: 3000 });
        fetch_data(
          {
            shiftPool: API_get_shift_pool_for_employee,
            shiftPickupResponses:
              API_get_employee_shift_pickup_request_responses,
            schedule: API_get_schedule,
          },
          api_params
        );
      }
    } catch (error) {
      // Handle network error
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }
      console.error("Error:", error); // Handle error as needed
    }
  };

  const create_pickup_request = async (shift_drop_id, shift_store_id) => {
    // Prepare data to send
    const requestData = {
      store_id: shift_store_id || storeId,
      shift_drop_id: shift_drop_id,
    };

    try {
      toast.loading("Submitting Shift Pickup Request...", { autoClose: false });

      const res = await postRightworkAPI(API_pickup_shift, requestData);

      if (res && res.status === 200) {
        // Handle success
        toast.dismiss();
        toast.success("Shift Pickup Request Submitted!", { autoClose: 3000 });
        fetch_data(
          {
            shiftPool: API_get_shift_pool_for_employee,
            shiftPickupResponses:
              API_get_employee_shift_pickup_request_responses,
            schedule: API_get_schedule,
          },
          api_params
        );
      }
    } catch (error) {
      // Handle network error
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }
      console.error("Error:", error); // Handle error as needed
    }
  };

  const rescind_pickup_shift = async (shift_drop_id, shift_store_id) => {
    // Prepare data to send
    const requestData = {
      store_id: shift_store_id || storeId,
      shift_drop_id: shift_drop_id,
    };

    try {
      toast.loading("Cancelling Shift Pickup Request...", { autoClose: false });

      const res = await postRightworkAPI(API_rescind_pickup_shift, requestData);

      if (res && res.status === 200) {
        // Handle success
        toast.dismiss();
        toast.success("Shift Pickup Request Cancelled!", { autoClose: 3000 });
        fetch_data(
          {
            shiftPool: API_get_shift_pool_for_employee,
            shiftPickupResponses:
              API_get_employee_shift_pickup_request_responses,
            schedule: API_get_schedule,
          },
          api_params
        );
      }
    } catch (error) {
      // Handle network error
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }
      console.error("Error:", error); // Handle error as needed
    }
  };

  const archive_shift_change_request = async (shift_change_request_uuid) => {
    // Prepare data to send
    const requestData = {
      store_id: storeId,
      shift_change_request_uuid: shift_change_request_uuid,
    };

    try {
      toast.loading("Archiving Shift Change Request...", { autoClose: false });

      const res = await postRightworkAPI(
        API_hide_shift_change_request,
        requestData
      );

      if (res && res.status === 200) {
        // Handle success
        toast.dismiss();
        fetch_data(
          {
            shiftPickupResponses:
              API_get_employee_shift_pickup_request_responses,
          },
          api_params
        );
      }
    } catch (error) {
      // Handle network error
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      fetch_data(
        {
          shiftPickupResponses: API_get_employee_shift_pickup_request_responses,
        },
        api_params
      );
      console.error("Error:", error); // Handle error as needed
    }
  };

  // Holds a submission that collided with an existing approved availability,
  // so we can confirm cancel-and-resubmit before retrying with replace_approved.
  const [replaceConfirmData, setReplaceConfirmData] = React.useState(null);

  const create_availability_request = async (data) => {
    /*
      store_id = request.data.get('store_id')
      dow = request.data.get('dow')
      available_from = request.data.get('available_from')
      available_to = request.data.get('available_to')
      preferred_from = request.data.get('preferred_from')
      preferred_to = request.data.get('preferred_to)
      note = request.data.get('note')
      user_id = request.user.id
    */
    // Prepare data to send
    const requestData = requestPayload(data, storeId);

    try {
      toast.loading("Submitting Availability Request...", { autoClose: false });

      const res = await postRightworkAPI(
        API_create_availability_request,
        requestData
      );

      if (res && res.status === 200) {
        // Handle success
        toast.dismiss();
        toast.success(requestDestinationMessage(requestData, storeId, props.storeNames), { autoClose: 3000 });
        fetch_data(
          {
            availability2: API_list_availability_for_employee,
          },
          api_params
        );
      }
    } catch (error) {
      // Handle network error
      toast.dismiss();

      // Already-approved availability for this day/effective date: offer to
      // cancel the existing one and resubmit, rather than just failing.
      if (error.response?.data?.system?.data?.code === "approved_exists") {
        setReplaceConfirmData(requestData);
        return;
      }

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error); // Handle error as needed
    }
  };

  const confirmReplaceAvailability = () => {
    const data = replaceConfirmData;
    setReplaceConfirmData(null);
    if (data) {
      create_availability_request({ ...data, replace_approved: true });
    }
  };

  const rescind_availability_request = async (
    default_availability_request_id
  ) => {
    // Prepare data to send
    const requestData = {
      store_id: storeId,
      default_availability_request_id: default_availability_request_id,
    };

    try {
      toast.loading("Cancelling Availability Request...", { autoClose: false });

      const res = await postRightworkAPI(
        API_rescind_availability_request,
        requestData
      );

      if (res && res.status === 200) {
        // Handle success
        toast.dismiss();
        toast.success("Availability Request Cancelled!", { autoClose: 3000 });
        fetch_data(
          {
            availability2: API_list_availability_for_employee,
          },
          api_params
        );
      }
    } catch (error) {
      // Handle network error
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error); // Handle error as needed
    }
  };


  const request_time_off = async (data) => {
    // Prepare data to send
    const requestData = requestPayload(data, storeId);

    try {
      toast.loading("Submitting Time Off Request...", { autoClose: false });

      const res = await postRightworkAPI(API_request_time_off, requestData);

      if (res && res.status === 200) {
        // Handle success
        toast.dismiss();
        toast.success(requestDestinationMessage(requestData, storeId, props.storeNames), { autoClose: 3000 });
        fetch_data(
          {
            timeOff: API_get_list_time_off_requests_for_employee,
          },
          api_params
        );
      }
    } catch (error) {
      // Handle network error
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error); // Handle error as needed
    }
  };

  const rescind_time_off_request = async (time_off_request_uuid) => {
    // Prepare data to send
    const requestData = {
      store_id: storeId,
      time_off_request_uuid: time_off_request_uuid,
    };

    try {
      toast.loading("Cancelling Time Off Request...", { autoClose: false });

      const res = await postRightworkAPI(
        API_rescind_time_off_request,
        requestData
      );

      if (res && res.status === 200) {
        // Handle success
        toast.dismiss();
        toast.success("Time Off Request Cancelled", { autoClose: 3000 });
        fetch_data(
          {
            timeOff: API_get_list_time_off_requests_for_employee,
          },
          api_params
        );
      }
    } catch (error) {
      // Handle network error
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error); // Handle error as needed
    }
  };

  const archive_time_off_request = async (time_off_request_uuid) => {
    // Prepare data to send
    const requestData = {
      store_id: storeId,
      time_off_request_uuid: time_off_request_uuid,
    };

    try {
      toast.loading("Archiving Time Off Request...", { autoClose: false });

      const res = await postRightworkAPI(
        API_hide_time_off_request,
        requestData
      );

      if (res && res.status === 200) {
        // Handle success
        toast.dismiss();
        fetch_data(
          {
            timeOff: API_get_list_time_off_requests_for_employee,
          },
          api_params
        );
      }
    } catch (error) {
      // Handle network error
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      fetch_data(
        {
          timeOff: API_get_list_time_off_requests_for_employee,
        },
        api_params
      );
      console.error("Error:", error); // Handle error as needed
    }
  };

  const archive_drawer_notification = async (drawer_notification_uuid) => {
    // Prepare data to send
    const requestData = {
      store_id: storeId,
      drawer_notification_uuid: drawer_notification_uuid,
    };

    try {
      toast.loading("Archiving Drawer Notification...", { autoClose: false });

      const res = await postRightworkAPI(
        API_hide_drawer_notification,
        requestData
      );

      if (res && res.status === 200) {
        // Handle success
        toast.dismiss();
        fetch_data(
          {
            notifications: API_drawer_notifications,
          },
          api_params
        );
      }
    } catch (error) {
      // Handle network error
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      fetch_data(
        {
          notifications: API_drawer_notifications,
        },
        api_params
      );
      console.error("Error:", error); // Handle error as needed
    }
  };

  const archive_all_drawer_notifications = async () => {
    // Prepare data to send
    const requestData = {
      store_id: storeId,
    };

    try {
      toast.loading("Archiving All Drawer Notifications...", {
        autoClose: false,
      });

      const res = await postRightworkAPI(
        API_hide_all_drawer_notifications,
        requestData
      );

      if (res.status === 200) {
        // Handle success
        toast.success("All notifications were cleared successfully", {
          autoClose: 3000,
        });
        toast.dismiss();
        fetch_data(
          {
            notifications: API_drawer_notifications,
          },
          api_params
        );
      }
    } catch (error) {
      // Handle network error
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      fetch_data(
        {
          notifications: API_drawer_notifications,
        },
        api_params
      );
      console.error("Error:", error); // Handle error as needed
    }
  };

  const update_notification_settings = async (
    fe_settings,
    role_filter,
    apply_to_all_stores = false
  ) => {
    // Prepare data to send
    const requestData = {
      store_id: storeId,
      fe_settings: JSON.stringify(fe_settings),
      apply_to_all_stores: apply_to_all_stores ? "true" : "false",
    };
    if (role_filter !== undefined) {
      requestData.role_filter = JSON.stringify(role_filter);
    }

    try {
      toast.loading("Updating Notification Settings...", { autoClose: false });

      const res = await postRightworkAPI(
        API_update_notification_settings,
        requestData
      );

      if (res && res.status === 200) {
        toast.dismiss();
        fetch_data(
          {
            notificationSettings: API_get_notification_settings,
          },
          api_params
        );
        const appliedToAllStores =
          res?.data?.redux?.payload?.applied_to_all_stores;
        toast.success(
          appliedToAllStores
            ? "Notification settings updated across all your stores"
            : "Your notification settings were successfully updated",
          { autoClose: 3000 }
        );
      }
    } catch (error) {
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      fetch_data(
        {
          notificationSettings: API_get_notification_settings,
        },
        api_params
      );
      console.error("Error:", error); // Handle error as needed
    }
  };

  // Resolves true once saved and the list has the new answers (so reopening
  // the task shows them), false on failure. A failure leaves the list alone:
  // the modal stays open holding the person's edits for another try.
  const save_plan_task_assignment = async (
    plan_task_assignment_uuid,
    plan_subtask_assignments,
    is_submit
  ) => {
    // Prepare data to send
    const requestData = {
      store_id: storeId,
      plan_task_assignment_uuid: plan_task_assignment_uuid,
      plan_subtask_assignments: JSON.stringify(plan_subtask_assignments),
      is_submit: is_submit,
    };

    try {
      toast.loading(
        `${is_submit ? "Submitting Answers" : "Saving Answers..."}`,
        { autoClose: false }
      );

      const res = await postRightworkAPI(
        API_save_plan_task_assignment,
        requestData
      );

      if (res && res.status === 200) {
        const refreshed = await fetch_data(
          {
            tasks: API_get_plan_tasks_for_user_and_date,
          },
          api_params
        );
        if (!refreshed) {
          toast.dismiss();
          toast.error("Saved, but your task list could not refresh. Check your connection.", {
            autoClose: 4000,
          });
          return false;
        }
        toast.dismiss();
        toast.success(
          `${
            is_submit
              ? "Answers Submitted Successfully"
              : "Answers Saved Successfully"
          }`,
          {
            autoClose: 3000,
          }
        );
        return true;
      }
    } catch (error) {
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }
      console.error("Error:", error); // Handle error as needed
    }
    return false;
  };

  // Get the current date
  let currentDate = new Date();
  // Yesterday
  let lowerBoundDate = new Date(currentDate);
  lowerBoundDate.setDate(currentDate.getDate() - 1);
  // 3 weeks out
  let upperBoundDate = new Date(currentDate);
  upperBoundDate.setDate(currentDate.getDate() + 21);

  let lowerBoundDateStr = formatDate(lowerBoundDate);
  let upperBoundDateStr = formatDate(upperBoundDate);

  let api_params = {
    store_id: storeId,
    lower_bound_date: lowerBoundDateStr,
    upper_bound_date: upperBoundDateStr,
    specific_date: formatDate(currentDate),
    date_start: formatDate(currentDate),
    date_end: formatDate(currentDate),
    should_return_all_stores: true
  };

  // Phase 1: Only what the My Day tab needs to render
  const phase1_api_calls = {
    schedule: API_get_schedule,
    roleNames: API_get_roles,
    tasks: API_get_plan_tasks_for_user_and_date,
  };

  // Phase 2: Everything else, fetched in parallel
  const phase2_api_calls = {
    availability2: API_list_availability_for_employee,
    timeOff: API_get_list_time_off_requests_for_employee,
    shiftPool: API_get_shift_pool_for_employee,
    shiftPickupResponses: API_get_employee_shift_pickup_request_responses,
    notifications: API_drawer_notifications,
    notificationSettings: API_get_notification_settings,
    blackoutDates: API_get_blackout_dates,
  };

  // Resolves true once every response is in, false on failure (a save
  // reports a refresh that did not land, so its modal does not close on a
  // stale list).
  const fetch_data = async (api_calls, api_params) => {
    let api_set_data_name = Object.keys(api_calls);
    let api_list = Object.values(api_calls);
    try {
      const res = await Promise.all(
        api_list.map((api) => getRightworkAPI(api, api_params))
      );
      setData((prevData) => {
        const newData = { ...prevData };
        res.forEach((res, i) => (newData[api_set_data_name[i]] = res.data));
        return newData;
      });
      return true;
    } catch (error) {
      // Handle error silently
      return false;
    }
  };

  React.useEffect(() => {
    if (typeof storeId !== "undefined") {
      // Fire both phases in parallel — Phase 1 renders My Day as soon as it resolves,
      // Phase 2 populates the other tabs independently
      fetch_data(phase1_api_calls, api_params);
      fetch_data(phase2_api_calls, api_params);
    }
  }, [storeId]);


  const scheduleData = data.schedule?.redux?.payload?.schedule;
  const myId = data.schedule?.redux?.payload?.me?.store_team_member?.id;
  const myUserId = data.schedule?.redux?.payload?.me?.store_team_member?.user_id;
  const minEffectiveDate = props.requestMinimumDaysInAdvance
    ? new Date(
        new Date().setDate(
          new Date().getDate() + props.requestMinimumDaysInAdvance
        )
      )
    : getMaxDatePlusOne(scheduleData);

  const firstName =
    data.schedule?.redux?.payload?.me?.store_team_member?.first_name;
  let myShifts = [];
  if (scheduleData) {
    myShifts = scheduleData.filter((v, i) => v.store_team_member?.user?.id === myUserId);
  }
  let myUpcomingShifts = myShifts.filter((shift) => {
    return shift.date >= formatDate(currentDate);
  });
  let notMyUpcomingShifts = [];
  if (scheduleData) {
    notMyUpcomingShifts = scheduleData.filter((shift) => {
      return (
        (shift.date >= formatDate(currentDate)) &&
        (shift.store_team_member?.user?.id !== myUserId)
      );
    });
  }

  const shiftPoolData = data.shiftPool?.redux?.payload?.schedule;
  let shiftPickupResponsesRes =
    data.shiftPickupResponses?.redux?.payload?.shift_pickup_responses;

  // const myUserId = data.shiftPickupResponses?.redux?.payload?.myUserId;

  let shiftPickupResponses = shiftPickupResponsesRes?.map((item) => {
    let requestType = "";

    if (
      item.employee_pickup &&
      item.employee_pickup.id === myUserId &&
      item.shift_pickup === null
    ) {
      requestType = "Pickup Request";
    } else if (
      item.employee_drop &&
      item.employee_drop.id === myUserId &&
      item.shift_pickup === null
    ) {
      requestType = "Add to Pool Request";
    } else if (
      item.employee_pickup &&
      item.employee_pickup.id === myUserId &&
      item.shift_pickup !== null
    ) {
      requestType = "tradeReceiver";
    } else if (
      item.employee_pickup &&
      item.employee_pickup.id !== myUserId &&
      item.shift_pickup !== null
    ) {
      requestType = "tradeSender";
    }

    return {
      ...item,
      requestType: requestType,
    };
  });

  const notifications =
    data.notifications?.redux?.payload?.drawer_notifications;

  const [isDrawerOpen, setDrawerOpen] = React.useState(false);

  const toggleDrawer = (open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }
    setDrawerOpen(open);
  };

  const blackoutDates = data.blackoutDates?.redux?.payload?.blackoutDates


  return (
    <RequestLocationProvider stores={props.storeNames} memberships={props.storeOptions} currentStoreId={storeId}>
      {myId ? (
        <div className="relative">
          <div className="w-full min-h-screen flex">
            <div className="w-1/5">
              <div className="h-full flex flex-col gap-2 px-6 py-5 bg-slate-50 border-r border-slate-200">
                <button
                  className={`mt-5 w-full py-3 px-4 text-left text-base flex items-center relative rounded-2xl ${
                    display === "My Day" ? "bg-white" : ""
                  }`}
                  onClick={() => setDisplay("My Day")}
                >
                  <div
                    className={`flex flex-1 items-center space-x-4 ${
                      display === "My Day"
                        ? "text-violet-700"
                        : "text-slate-500"
                    }`}
                  >
                    <div>
                      <CalendarTodayIcon />
                    </div>
                    <span>My Day</span>
                  </div>
                </button>
                {tasksEnabled && (
                  <button
                    className={`w-full py-3 px-4 text-left flex items-center relative rounded-2xl ${
                      display === "Tasks" ? "bg-white" : ""
                    }`}
                    onClick={() => setDisplay("Tasks")}
                  >
                    <div
                      className={`flex flex-1 items-center space-x-4 ${
                        display === "Tasks" ? "text-violet-700" : "text-slate-500"
                      }`}
                    >
                      <div>
                        <AssignmentTurnedInOutlinedIcon />
                      </div>
                      <span>Tasks</span>
                    </div>
                  </button>
                )}
                <button
                  className={`w-full py-3 px-4 text-left flex items-center relative rounded-2xl ${
                    display === "Availability" ? "bg-white" : ""
                  }`}
                  onClick={() => setDisplay("Availability")}
                >
                  <div
                    className={`flex flex-1 items-center space-x-4 ${
                      display === "Availability"
                        ? "text-violet-700"
                        : "text-slate-500"
                    }`}
                  >
                    <div>
                      <BackHandOutlinedIcon />
                    </div>
                    <span>Availability</span>
                  </div>
                </button>
                <button
                  className={`w-full py-3 px-4 text-left flex items-center relative rounded-2xl ${
                    display === "Time Off" ? "bg-white" : ""
                  }`}
                  onClick={() => setDisplay("Time Off")}
                >
                  <div
                    className={`flex flex-1 items-center space-x-4 ${
                      display === "Time Off"
                        ? "text-violet-700"
                        : "text-slate-500"
                    }`}
                  >
                    <div>
                      <ScheduleOutlinedIcon />
                    </div>
                    <span>Time Off</span>
                  </div>
                </button>
                <button
                  className={`w-full py-3 px-4 text-left flex items-center relative rounded-2xl ${
                    display === "Shift Swaps" ? "bg-white" : ""
                  }`}
                  onClick={() => setDisplay("Shift Swaps")}
                >
                  <div
                    className={`flex flex-1 items-center space-x-4 ${
                      display === "Shift Swaps"
                        ? "text-violet-700"
                        : "text-slate-500"
                    }`}
                  >
                    <div>
                      <SwapHorizIcon />
                    </div>
                    <span>Shift Swaps</span>
                  </div>
                </button>
              </div>
            </div>
            <div className="w-4/5">
              <div>
                <TopBarDisplayTitle
                  title={display}
                  storeOptions={props.storeOptions}
                  homeStore={props.homeStore}
                  storeNames={props.storeNames}
                  change_home_store={props.change_home_store}
                />
              </div>
              {display === "My Day" ? (
                <HomeDisplayEmployee
                  scheduleData={scheduleData}
                  myUpcomingShifts={myUpcomingShifts}
                  notMyUpcomingShifts={notMyUpcomingShifts}
                  myId={myId}
                  myUserId={myUserId}
                  firstName={firstName}
                  drop_shift={drop_shift}
                  trade_shift={trade_shift}
                  rescind_drop_shift={rescind_drop_shift}
                  rescind_trade_shift={rescind_trade_shift}
                  roleNames={data.roleNames}
                  tasks={tasksEnabled ? data?.tasks?.redux?.payload?.plan_task_assignments : []}
                  save_plan_task_assignment={save_plan_task_assignment}
                  refreshOccurrences={() =>
                    fetch_data({ tasks: API_get_plan_tasks_for_user_and_date }, api_params)
                  }
                  homeCompany={props.homeCompany}
                  onOpenTasks={tasksEnabled ? () => setDisplay("Tasks") : undefined}
                />
              ) : display === "Tasks" && tasksEnabled ? (
                <TasksDisplayEmployee
                  storeId={storeId}
                  homeCompany={props.homeCompany}
                  roleNames={data.roleNames}
                  occurrences={data?.tasks?.redux?.payload?.plan_task_assignments}
                  save_plan_task_assignment={save_plan_task_assignment}
                  refreshOccurrences={() =>
                    fetch_data({ tasks: API_get_plan_tasks_for_user_and_date }, api_params)
                  }
                />
              ) : display === "Availability" ? (
                data.availability2 ? (
                  <AvailabilityDisplayEmployee
                    availabilityData2={data.availability2?.redux?.payload}
                    timeOffData={data.timeOff?.redux?.payload?.timeOffRequests}
                    myId={myId}
                    create_availability_request={create_availability_request}
                    rescind_availability_request={rescind_availability_request}
                    request_time_off={request_time_off}
                    rescind_time_off_request={rescind_time_off_request}
                    archive_time_off_request={archive_time_off_request}
                    permissionRole={props.permissionRole}
                    minEffectiveDate={minEffectiveDate}
                    storeTimezone={storeTimezone}
                  />
                ) : (
                  <div className="w-full h-96 flex items-center justify-center">
                    <LoadSpinner />
                  </div>
                )
              ) : display === "Time Off" ? (
                data.timeOff ? (
                  <TimeOffDisplayEmployee
                    availabilityData2={data.availability2?.redux?.payload}
                    timeOffData={data.timeOff?.redux?.payload?.timeOffRequests}
                    myId={myId}
                    create_availability_request={create_availability_request}
                    rescind_availability_request={rescind_availability_request}
                    request_time_off={request_time_off}
                    rescind_time_off_request={rescind_time_off_request}
                    archive_time_off_request={archive_time_off_request}
                    permissionRole={props.permissionRole}
                    minEffectiveDate={minEffectiveDate}
                    blackoutDates={blackoutDates}
                    canRequestPto={data.schedule?.redux?.payload?.me?.store_team_member?.can_request_pto}
                    canRequestSicktime={data.schedule?.redux?.payload?.me?.store_team_member?.can_request_sicktime}
                    storeTimezone={storeTimezone}
                  />
                ) : (
                  <div className="w-full h-96 flex items-center justify-center">
                    <LoadSpinner />
                  </div>
                )
              ) : display === "Shift Swaps" ? (
                data.shiftPool && data.shiftPickupResponses ? (
                  <ShiftSwapDisplayEmployee
                    shiftPoolData={shiftPoolData}
                    scheduleData={scheduleData}
                    shiftPickupResponses={shiftPickupResponses}
                    myShifts={myShifts}
                    myUpcomingShifts={myUpcomingShifts}
                    notMyUpcomingShifts={notMyUpcomingShifts}
                    myId={myId}
                    myUserId={myUserId}
                    drop_shift={drop_shift}
                    trade_shift={trade_shift}
                    rescind_drop_shift={rescind_drop_shift}
                    rescind_trade_shift={rescind_trade_shift}
                    respond_to_trade_request={respond_to_trade_request}
                    create_pickup_request={create_pickup_request}
                    rescind_pickup_shift={rescind_pickup_shift}
                    archive_shift_change_request={archive_shift_change_request}
                  />
                ) : (
                  <div className="w-full h-96 flex items-center justify-center">
                    <LoadSpinner />
                  </div>
                )
              ) : (
                <div></div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <LoadSpinner />
        </div>
      )}
      <NotificationsDrawer
        open={isDrawerOpen}
        onClose={toggleDrawer(false)}
        notifications={notifications}
        archive_drawer_notification={archive_drawer_notification}
        archive_all_drawer_notifications={archive_all_drawer_notifications}
        notificationSettings={
          data?.notificationSettings?.redux?.payload
        }
        update_notification_settings={update_notification_settings}
        permissionRole={props.permissionRole}
      />

      <Modal
        open={Boolean(replaceConfirmData)}
        onClose={() => setReplaceConfirmData(null)}
        className="flex items-center justify-center"
      >
        <Box
          sx={{ outline: "none" }}
          className="bg-white rounded-2xl w-[90%] max-w-[420px] p-6"
        >
          <h3 className="text-lg font-bold text-slate-900">
            Replace approved availability?
          </h3>
          <p className="text-sm text-slate-500 mt-2">
            You already have approved availability for this day. Would you like
            to cancel it and submit this new availability for approval?
          </p>
          <div className="mt-6 flex items-center justify-end space-x-3">
            <button
              type="button"
              onClick={() => setReplaceConfirmData(null)}
              className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirmReplaceAvailability}
              className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-violet-700 hover:bg-violet-800"
            >
              Cancel &amp; Resubmit
            </button>
          </div>
        </Box>
      </Modal>
    </RequestLocationProvider>
  );
}
